/**
 * Canvas Yjs WebSocket Provider — connects directly to the Canvas Relay service
 * for real-time Yjs binary sync.
 *
 * User connections authenticate via JWT token in query param.
 * Public viewers use the share WebSocket endpoint (no auth).
 */

import { MSG_SYNC, MSG_AWARENESS, MSG_PRESENCE, MSG_EDITORS, MSG_SYNC_COMPLETE } from "./constants";

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const DEFAULT_MAX_RECONNECT_ATTEMPTS = 0; // 0 = unlimited
const ACTION_TIMEOUT_MS = 30_000;
const CLOSE_SHARE_REVOKED = 4001;
const SYNC_FALLBACK_TIMEOUT_MS = 2000;
export type SyncHandler = (data: Uint8Array) => void;
export type AwarenessHandler = (data: Uint8Array) => void;
export type PresenceHandler = (data: Uint8Array) => void;
export type EditorsHandler = (data: Uint8Array) => void;
export type ConnectionHandler = (connected: boolean) => void;
export type ErrorHandler = (reason: string) => void;
export type TokenRefreshHandler = () => Promise<string>;

export interface ActionResultMessage {
  type: "action:result";
  requestId: string;
  status: string;
  data?: Record<string, unknown>;
}

export interface ActionErrorMessage {
  type: "action:error";
  requestId: string;
  error: string;
}

export type ActionResultHandler = (msg: ActionResultMessage) => void;
export type ActionErrorHandler = (msg: ActionErrorMessage) => void;
export type ShareRevokedHandler = () => void;
export type SyncCompleteHandler = () => void;
export type ReconnectingHandler = () => void;

export interface CanvasYjsProviderOptions {
  /** Direct Canvas Relay WebSocket URL, e.g. wss://canvas-relay.beeos.ai */
  relayUrl: string;
  /** JWT token for user authentication (passed as query param) */
  token?: string;
  role?: "editor" | "viewer";
  /** If set, connects to the public share endpoint instead */
  shareSlug?: string;
  /** Self-reported display name for presence */
  displayName?: string;
  /** Self-reported avatar URL for presence */
  avatarUrl?: string;
  /** Stop reconnecting after this many consecutive failures (0 = unlimited) */
  maxReconnectAttempts?: number;
}

export class CanvasYjsProvider {
  private ws: WebSocket | null = null;
  private opts: CanvasYjsProviderOptions;
  private canvasId: string | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;

  private onSync: SyncHandler | null = null;
  private onAwareness: AwarenessHandler | null = null;
  private onPresence: PresenceHandler | null = null;
  private onEditors: EditorsHandler | null = null;
  private onConnection: ConnectionHandler | null = null;
  private onError: ErrorHandler | null = null;
  private onTokenRefresh: TokenRefreshHandler | null = null;
  private onActionResult: ActionResultHandler | null = null;
  private onActionError: ActionErrorHandler | null = null;
  private onShareRevoked: ShareRevokedHandler | null = null;
  private onSyncComplete: SyncCompleteHandler | null = null;
  private onReconnecting: ReconnectingHandler | null = null;
  private initialConnectDone = false;
  private syncBuffer: Uint8Array[] = [];
  private synced = false;
  private syncFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingMessages: Uint8Array[] = [];
  private pendingTextMessages: string[] = [];
  private actionTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private connectOpenedAt = 0;
  private consecutiveQuickCloses = 0;
  private static QUICK_CLOSE_THRESHOLD_MS = 2000;
  private static QUICK_CLOSE_REFRESH_AFTER = 1;

  constructor(opts: CanvasYjsProviderOptions) {
    this.opts = opts;
  }

  /** Update the token (e.g. after refresh) without reconnecting. */
  updateToken(token: string): void {
    this.opts = { ...this.opts, token };
  }

  connect(
    canvasId: string,
    handlers: {
      onSync: SyncHandler;
      onAwareness?: AwarenessHandler;
      onPresence?: PresenceHandler;
      onEditors?: EditorsHandler;
      onConnection?: ConnectionHandler;
      onError?: ErrorHandler;
      onTokenRefresh?: TokenRefreshHandler;
      onActionResult?: ActionResultHandler;
      onActionError?: ActionErrorHandler;
      onShareRevoked?: ShareRevokedHandler;
      onSyncComplete?: SyncCompleteHandler;
      onReconnecting?: ReconnectingHandler;
    },
  ): void {
    this.canvasId = canvasId;
    this.onSync = handlers.onSync;
    this.onAwareness = handlers.onAwareness ?? null;
    this.onPresence = handlers.onPresence ?? null;
    this.onEditors = handlers.onEditors ?? null;
    this.onConnection = handlers.onConnection ?? null;
    this.onError = handlers.onError ?? null;
    this.onTokenRefresh = handlers.onTokenRefresh ?? null;
    this.onActionResult = handlers.onActionResult ?? null;
    this.onActionError = handlers.onActionError ?? null;
    this.onShareRevoked = handlers.onShareRevoked ?? null;
    this.onSyncComplete = handlers.onSyncComplete ?? null;
    this.onReconnecting = handlers.onReconnecting ?? null;
    this.closed = false;
    this.initialConnectDone = false;
    this.reconnectAttempts = 0;
    this.consecutiveQuickCloses = 0;
    this.doConnect();
  }

  private resetSyncState(): void {
    this.synced = false;
    this.syncBuffer = [];
    if (this.syncFallbackTimer) {
      clearTimeout(this.syncFallbackTimer);
      this.syncFallbackTimer = null;
    }
  }

  private flushSyncBuffer(): void {
    if (this.closed || this.synced) return;
    if (this.syncFallbackTimer) {
      clearTimeout(this.syncFallbackTimer);
      this.syncFallbackTimer = null;
    }
    this.synced = true;
    const buffered = this.syncBuffer;
    this.syncBuffer = [];
    for (const data of buffered) {
      this.onSync?.(data);
    }
    this.onSyncComplete?.();
  }

  private doConnect(): void {
    if (this.closed || !this.canvasId) return;

    if (this.initialConnectDone) {
      this.onReconnecting?.();
    }
    this.initialConnectDone = true;
    this.resetSyncState();
    this.syncFallbackTimer = setTimeout(() => {
      if (!this.closed && !this.synced) this.flushSyncBuffer();
    }, SYNC_FALLBACK_TIMEOUT_MS);

    let url: string;
    if (this.opts.shareSlug) {
      url = `${this.opts.relayUrl}/ws/share/${this.opts.shareSlug}`;
      if (this.opts.token) {
        url += `?token=${encodeURIComponent(this.opts.token)}`;
      }
    } else {
      const params = new URLSearchParams();
      if (this.opts.token) params.set("token", this.opts.token);
      url = `${this.opts.relayUrl}/ws/${this.canvasId}?${params.toString()}`;
    }

    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.consecutiveQuickCloses = 0;
      this.connectOpenedAt = Date.now();
      this.ws = ws;
      this.onConnection?.(true);

      if (this.opts.displayName || this.opts.avatarUrl) {
        ws.send(JSON.stringify({
          type: "profile",
          displayName: this.opts.displayName,
          avatarUrl: this.opts.avatarUrl,
        }));
      }

      for (const msg of this.pendingMessages) {
        ws.send(msg);
      }
      this.pendingMessages = [];
      for (const msg of this.pendingTextMessages) {
        ws.send(msg);
      }
      this.pendingTextMessages = [];
    };

    ws.onmessage = (event: MessageEvent) => {
      if (this.closed) return;
      if (typeof event.data === "string") {
        this.handleTextMessage(event.data);
        return;
      }
      const data = new Uint8Array(event.data as ArrayBuffer);
      if (data.length === 0) return;

      const msgType = data[0];
      if (msgType === MSG_SYNC_COMPLETE) {
        this.flushSyncBuffer();
      } else if (msgType === MSG_AWARENESS && this.onAwareness) {
        this.onAwareness(data);
      } else if (msgType === MSG_PRESENCE && this.onPresence) {
        this.onPresence(data);
      } else if (msgType === MSG_EDITORS && this.onEditors) {
        this.onEditors(data);
      } else if (msgType === MSG_SYNC) {
        if (!this.synced) {
          this.syncBuffer.push(data);
        } else {
          this.onSync?.(data);
        }
      }
    };

    ws.onclose = (event: CloseEvent) => {
      const wasOpen = this.ws === ws;
      this.ws = null;
      this.onConnection?.(false);
      if (event.code === CLOSE_SHARE_REVOKED) {
        this.closed = true;
        this.onShareRevoked?.();
        return;
      }

      const livedMs = this.connectOpenedAt > 0
        ? Date.now() - this.connectOpenedAt
        : Infinity;
      const isQuickClose = !wasOpen || livedMs < CanvasYjsProvider.QUICK_CLOSE_THRESHOLD_MS;
      if (isQuickClose) {
        this.consecutiveQuickCloses++;
      } else {
        this.consecutiveQuickCloses = 0;
      }

      if (
        isQuickClose &&
        this.consecutiveQuickCloses >= CanvasYjsProvider.QUICK_CLOSE_REFRESH_AFTER &&
        this.onTokenRefresh
      ) {
        this.onTokenRefresh().then((newToken) => {
          if (this.closed) return;
          if (newToken) this.opts = { ...this.opts, token: newToken };
          this.scheduleReconnect();
        }).catch(() => {
          if (!this.closed) this.scheduleReconnect();
        });
        return;
      }

      this.scheduleReconnect();
    };

    ws.onerror = () => {
      ws.close();
    };
  }

  private scheduleReconnect(): void {
    if (this.closed) return;

    const maxAttempts =
      this.opts.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS;
    if (maxAttempts > 0 && this.reconnectAttempts >= maxAttempts) {
      this.onError?.("max_reconnect_attempts");
      return;
    }

    const delay = Math.min(
      RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts),
      RECONNECT_MAX_MS,
    );
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.doConnect();
    }, delay);
  }

  send(data: Uint8Array): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else {
      this.pendingMessages.push(data);
    }
  }

  sendText(text: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(text);
    } else {
      this.pendingTextMessages.push(text);
    }
  }

  sendAction(
    surfaceId: string,
    userAction: {
      name: string;
      sourceComponentId: string;
      timestamp: string;
      context: Record<string, unknown>;
    },
  ): string {
    const requestId = `act_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    this.sendText(
      JSON.stringify({
        type: "action",
        surfaceId,
        userAction,
        requestId,
      }),
    );
    const timer = setTimeout(() => {
      this.actionTimers.delete(requestId);
      this.onActionError?.({ type: "action:error", requestId, error: "timeout" });
    }, ACTION_TIMEOUT_MS);
    this.actionTimers.set(requestId, timer);
    return requestId;
  }

  private handleTextMessage(text: string): void {
    try {
      const msg = JSON.parse(text) as Record<string, unknown>;
      const type = msg.type as string;
      const reqId = msg.requestId as string | undefined;
      if (reqId) {
        const timer = this.actionTimers.get(reqId);
        if (timer) {
          clearTimeout(timer);
          this.actionTimers.delete(reqId);
        }
      }
      if (type === "action:result" && this.onActionResult) {
        this.onActionResult(msg as unknown as ActionResultMessage);
      } else if (type === "action:error" && this.onActionError) {
        this.onActionError(msg as unknown as ActionErrorMessage);
      }
    } catch {
      // ignore unparseable text frames
    }
  }

  disconnect(): void {
    this.closed = true;
    this.resetSyncState();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
    this.canvasId = null;
    this.pendingMessages = [];
    this.pendingTextMessages = [];
    for (const timer of this.actionTimers.values()) clearTimeout(timer);
    this.actionTimers.clear();
    this.onConnection?.(false);
    this.onSync = null;
    this.onAwareness = null;
    this.onPresence = null;
    this.onEditors = null;
    this.onConnection = null;
    this.onError = null;
    this.onTokenRefresh = null;
    this.onActionResult = null;
    this.onActionError = null;
    this.onShareRevoked = null;
    this.onSyncComplete = null;
    this.onReconnecting = null;
  }

  get connected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  get currentCanvasId(): string | null {
    return this.canvasId;
  }
}
